import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  UserProfileDto,
  UserRole,
  CreateStakeholderDto,
  StakeholderDto as ApiStakeholderDto,
} from "@green0meter/api-client";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";

import { DOMAIN_DOUBLE_MATERIALITY } from "../domain";
import { ACTION_GET_STAKEHOLDERS } from "../GetDMStakeholders/useGetDMStakeholders";

import { roleGuard } from "@api";
import { doubleMaterialityStakeholdersApi } from "@api/services";

export const ACTION_CREATE_STAKEHOLDER = "CREATE_STAKEHOLDER";

const allowedRoles = [UserRole.Owner, UserRole.Manager];

interface CreateStakeholderResponse {
  data: ApiStakeholderDto;
}

const createStakeholder = async (
  data: CreateStakeholderDto,
): Promise<CreateStakeholderResponse> => {
  const response =
    await doubleMaterialityStakeholdersApi.doubleMaterialityStakeholdersControllerCreateStakeholder(
      data,
    );

  return {
    data: response.data,
  };
};

export const useCreateStakeholder = (
  user: UserProfileDto | null,
): {
  createStakeholder: (data: CreateStakeholderDto) => void;
  createStakeholderAsync: (
    data: CreateStakeholderDto,
  ) => Promise<CreateStakeholderResponse>;
  isPendingCreateStakeholder: boolean;
  isSuccessCreateStakeholder: boolean;
  isErrorCreateStakeholder: boolean;
  errorCreateStakeholder: Error | null;
  enabled: boolean;
} => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const enabled = roleGuard(user, allowedRoles);

  const mutation = useMutation({
    mutationFn: createStakeholder,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [DOMAIN_DOUBLE_MATERIALITY, ACTION_GET_STAKEHOLDERS],
      });
      enqueueSnackbar(t("stakeholders.messages.createSuccess"), {
        variant: "success",
      });
    },
    onError: (error: Error) => {
      enqueueSnackbar(error.message || t("stakeholders.messages.createError"), {
        variant: "error",
      });
    },
  });

  return {
    createStakeholder: mutation.mutate,
    createStakeholderAsync: mutation.mutateAsync,
    isPendingCreateStakeholder: mutation.isPending,
    isSuccessCreateStakeholder: mutation.isSuccess,
    isErrorCreateStakeholder: mutation.isError,
    errorCreateStakeholder: mutation.error,
    enabled,
  };
};
